import { z } from 'zod';

/** Per-tenant cashback configuration (stored in `settings` under key `cashback`). */
export interface CashbackConfig {
  /** Percent of the eligible subtotal earned as cashback (money). */
  earnRatePct: number;
  /** Minimum balance required before any redemption. */
  minRedeem: string;
  /** Max share of an order that can be paid with cashback (0–100). */
  maxRedeemPct: number;
  channels: { pos: boolean; invoice: boolean; online: boolean };
  /** Months until earned cashback expires; null = never. */
  expiryMonths: number | null;
  /** Post earn/redeem to the General Ledger (loyalty liability). */
  postToGl: boolean;
  liabilityAccountId: string | null;
  expenseAccountId: string | null;
  /**
   * Welcome credit granted ONCE when a customer registers on the app/store
   * (base currency). '0' turns the gift off.
   */
  registrationBonus: string;
  /**
   * Cashback for customers with NO customer group — "normal" shoppers, which is
   * every self-service app signup.
   *
   * These two live here and not on `price_lists` for the plain reason that a
   * normal customer HAS no price-list row to carry a flag. Every other group is
   * switched on its own row (see PriceList.hasCashback), so the shop sets one
   * rule per group and this pair is the one that has nowhere else to go.
   */
  normalEarnsCashback: boolean;
  normalGetsRegistrationBonus: boolean;
  /**
   * @deprecated Replaced by `normalGetsRegistrationBonus` plus the per-group flag
   * on `price_lists`. Only still read so a config saved by the short-lived
   * array version resolves to the same answer instead of silently reverting to
   * the default. Never written.
   */
  registrationBonusGroups?: string[];
}

/**
 * Stands for "a customer with no price list" wherever a group has to be named
 * as a string. Not a uuid, so it can never collide with a real price-list id.
 */
export const NORMAL_GROUP = 'normal';

const MONEY = /^\d+(\.\d{1,4})?$/;
const SIGNED_MONEY = /^-?\d+(\.\d{1,4})?$/;

export const CashbackConfigUpdateSchema = z.object({
  earnRatePct: z.number().min(0).max(100).optional(),
  minRedeem: z.string().regex(MONEY).optional(),
  maxRedeemPct: z.number().min(0).max(100).optional(),
  channels: z.object({ pos: z.boolean(), invoice: z.boolean(), online: z.boolean() }).optional(),
  expiryMonths: z.number().int().min(1).max(120).nullable().optional(),
  postToGl: z.boolean().optional(),
  liabilityAccountId: z.string().uuid().nullable().optional(),
  expenseAccountId: z.string().uuid().nullable().optional(),
  registrationBonus: z.string().regex(MONEY).optional(),
  // Group-less customers only; every other group is switched on its own
  // price-list row.
  normalEarnsCashback: z.boolean().optional(),
  normalGetsRegistrationBonus: z.boolean().optional(),
});
export type CashbackConfigUpdate = z.infer<typeof CashbackConfigUpdateSchema>;

export interface CashbackBalance {
  partyId: string;
  partyCode: string;
  partyName: string;
  balance: string;
  lifetimeEarned: string;
  lifetimeRedeemed: string;
  updatedAt: string | null;
}

export type CashbackTxnType = 'earn' | 'redeem' | 'adjust' | 'expire' | 'reverse';

export interface CashbackLedgerEntry {
  id: string;
  partyId: string;
  partyName: string;
  type: CashbackTxnType;
  amount: string; // signed, base currency
  balanceAfter: string;
  sourceType: string | null;
  sourceId: string | null;
  /**
   * The source document's HUMAN number (e.g. "SO-000042") — what a shop actually
   * calls the transaction. Without it the ledger could only say "sales_order"
   * and a uuid, so a row could not be tied back to the sale that earned it
   * without a database. Null when the document has since been deleted, or for a
   * manual adjustment that has no document at all.
   */
  sourceNumber: string | null;
  /** The customer's code, so a row identifies the person the way the shop does. */
  partyCode: string | null;
  journalEntryId: string | null;
  note: string | null;
  createdAt: string;
}

export interface CashbackSummary {
  outstandingLiability: string;
  lifetimeEarned: string;
  lifetimeRedeemed: string;
  customers: number;
}

/** Manual balance adjustment (signed money; +grant / −revoke). */
export const CashbackAdjustSchema = z.object({
  partyId: z.string().uuid(),
  amount: z.string().regex(SIGNED_MONEY),
  note: z.string().max(500).optional(),
});
export type CashbackAdjust = z.infer<typeof CashbackAdjustSchema>;

/** Redeem cashback against a posted invoice (creates a cashback payment). */
export const CashbackRedeemSchema = z.object({
  invoiceId: z.string().uuid(),
  amount: z.string().regex(MONEY),
});
export type CashbackRedeem = z.infer<typeof CashbackRedeemSchema>;
